import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Textarea } from "@/project/karuna/components/ui/textarea"; // @ts-ignore - textarea is not a valid module
import {
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";
import {
  applySchoolProfileStatus,
  getSchoolProfileStatus,
  normalizeSchoolApprovalStatus,
} from "@/project/karuna/utils/schoolProfileApproval";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore - dialog is not a valid module
import { DynamicModuleService } from "@/services/APIs";
import { removeMetaFields } from "@/lib/removeMetaFields";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import { fetchSchoolProfileGridRecords } from "@/project/karuna/utils/fetchSchemaGridRecords";

const SCHEMA_NAME = "School Profile";

/** Grid rows may omit columns; values can live under basicInfo, nested data, or dotted keys. */
function extractStringFieldFromRecord(
  record: Record<string, unknown>,
  possibleNames: string[]
): string {
  let parsedData: unknown = record?.data;
  if (typeof parsedData === "string") {
    try {
      parsedData = JSON.parse(parsedData) as Record<string, unknown>;
    } catch {
      parsedData = undefined;
    }
  }

  const recordSources: Record<string, unknown>[] = [
    record,
    parsedData as Record<string, unknown>,
    record.basicInfo as Record<string, unknown>,
    record.basic_info as Record<string, unknown>,
    record.basic_grid as Record<string, unknown>,
    record.basicGrid as Record<string, unknown>,
    (parsedData as Record<string, unknown> | undefined)?.basicInfo,
    (parsedData as Record<string, unknown> | undefined)?.basic_info,
    (parsedData as Record<string, unknown> | undefined)?.basic_grid,
    (parsedData as Record<string, unknown> | undefined)?.basicGrid,
  ].filter(
    (s): s is Record<string, unknown> =>
      s !== null &&
      s !== undefined &&
      typeof s === "object" &&
      !Array.isArray(s)
  );

  for (const src of recordSources) {
    for (const name of possibleNames) {
      const v = (src as Record<string, unknown>)[name];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v).trim();
      }
      const lowerName = name.toLowerCase();
      for (const key of Object.keys(src)) {
        if (key.toLowerCase() === lowerName) {
          const vv = (src as Record<string, unknown>)[key];
          if (vv !== undefined && vv !== null && String(vv).trim() !== "") {
            return String(vv).trim();
          }
        }
      }
    }
  }

  for (const root of [record, parsedData as Record<string, unknown>]) {
    if (!root || typeof root !== "object" || Array.isArray(root)) {
      continue;
    }
    for (const key of Object.keys(root)) {
      if (!key.includes(".")) {
        continue;
      }
      const last = key.split(".").pop() ?? "";
      for (const name of possibleNames) {
        if (last.toLowerCase() !== name.toLowerCase()) {
          continue;
        }
        const v = (root as Record<string, unknown>)[key];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          return String(v).trim();
        }
      }
    }
  }

  return "";
}

/** Single-line label + value; avoids grid overlap on narrow cards */
function ApprovalFieldRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 break-words text-sm leading-snug text-foreground">
        {children ?? "—"}
      </div>
    </div>
  );
}

interface School {
  id: string;
  kcno?: string;
  schoolname?: string;
  principal?: string;
  contact?: string;
  schoolemail?: string;
  kendra?: string;
  address1?: string;
  address2?: string;
  city?: string;
  pincode?: string;
  state?: string;
  dateofformation?: string;
  contactfax?: string;
  emailid?: string;
  mobileno?: string;
  additionaldetails?: string;
  teachername?: string;
  teachercontact?: string;
  teacheremail?: string;
  status?: string;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
}

export default function AdminSchoolApproval() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const [schools, setSchools] = useState<School[]>([]);
  const [filteredSchools, setFilteredSchools] = useState<School[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Determine initial tab and view mode from URL params
  const tabParam = searchParams.get("tab");
  const viewParam = searchParams.get("view");
  const isHistoryView = viewParam === "history";
  const isPendingOnlyView = tabParam === "pending";

  // Set initial active tab based on URL params
  const [activeTab, setActiveTab] = useState<
    "pending" | "approved" | "rejected"
  >(() => {
    const tab = searchParams.get("tab");
    const view = searchParams.get("view");
    if (tab === "pending" || tab === "approved" || tab === "rejected") {
      return tab;
    }
    if (view === "history") {
      return "approved";
    }
    return "pending";
  });

  // Update active tab when URL params change
  useEffect(() => {
    const tab = searchParams.get("tab");
    const view = searchParams.get("view");
    if (tab === "pending" || tab === "approved" || tab === "rejected") {
      setActiveTab(tab);
    } else if (view === "history") {
      setActiveTab("approved");
    } else if (!tab && !view) {
      setActiveTab("pending");
    }
  }, [searchParams]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      // grid_data rows omit `status`; merged fetch pulls it from the config list API
      const allRecords: any[] = await fetchSchoolProfileGridRecords();
      if (import.meta.env.DEV) {
        console.log(
          `✅ fetchSchoolProfileGridRecords: Found ${allRecords.length} records`
        );
      }

      if (import.meta.env.DEV && allRecords.length > 0) {
        console.log("📋 First record keys:", Object.keys(allRecords[0]));
      }

      const principalFieldNames = [
        "principal",
        "principalname",
        "principal_name",
        "principalName",
        "Principal",
        "PrincipalName",
      ];

      // Map records to School interface
      // Handle different field name variations (case-insensitive, different naming)
      const mappedSchools: School[] = allRecords.map((record: any) => {
        const getField = (possibleNames: string[]): string =>
          extractStringFieldFromRecord(record, possibleNames);

        const mapped: School = {
          id: record.id ?? record.pk ?? record._id ?? "",
          kcno: getField(["kcno", "kc_no", "kcNo", "KCNo"]),
          schoolname: getField([
            "schoolname",
            "school_name",
            "schoolName",
            "SchoolName",
          ]),
          principal: getField(principalFieldNames),
          contact: getField([
            "contact",
            "schoolcontact",
            "contact_number",
            "contactNumber",
            "Contact",
          ]),
          schoolemail: getField([
            "schoolemail",
            "school_email",
            "schoolEmail",
            "SchoolEmail",
            "email",
          ]),
          kendra: getField([
            "kendra",
            "nearestkendra",
            "kendra_name",
            "kendraName",
            "Kendra",
          ]),
          teachername: getField([
            "teachername",
            "teacher_name",
            "teacherName",
            "TeacherName",
          ]),
          teachercontact: getField([
            "teachercontact",
            "teacher_contact",
            "teachermobile",
            "teacherContact",
            "teacher_contact_number",
            "TeacherContact",
          ]),
          teacheremail: getField([
            "teacheremail",
            "teacher_email",
            "teacherEmail",
            "TeacherEmail",
          ]),
          address1: getField(["address1", "address_1"]),
          address2: getField(["address2", "address_2"]),
          city: getField(["city"]),
          pincode: getField(["pincode", "pin_code"]),
          state: getField(["state"]),
          dateofformation: getField(["dateofformation", "date_of_formation"]),
          contactfax: getField(["contactfax", "contact_fax"]),
          emailid: getField(["emailid", "email_id"]),
          mobileno: getField(["mobileno", "mobile_no"]),
          additionaldetails: getField([
            "additionaldetails",
            "additional_details",
          ]),
          status: normalizeSchoolApprovalStatus(
            getSchoolProfileStatus(record) ||
              getField([
                "status",
                "Status",
                "approval_status",
                "approvalStatus",
              ])
          ),
          rejection_reason: getField([
            "rejection_reason",
            "rejectionReason",
            "rejection_reason",
          ]),
          created_at: getField(["created_at", "createdAt", "created"]),
          updated_at: getField(["updated_at", "updatedAt", "updated"]),
        };

        return mapped;
      });

      if (import.meta.env.DEV) {
        console.log(`✅ Total mapped schools: ${mappedSchools.length}`);
      }
      setSchools(mappedSchools);
    } catch (error: any) {
      console.error("❌ Error fetching schools:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterSchools = useCallback(() => {
    let filtered = schools.filter(school => {
      const status = normalizeSchoolApprovalStatus(school.status ?? "");
      return status === activeTab;
    });

    if (searchTerm) {
      filtered = filtered.filter(
        school =>
          (school.schoolname ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (school.kcno ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (school.schoolemail ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (school.contact ?? "").includes(searchTerm)
      );
    }

    setFilteredSchools(filtered);
  }, [schools, searchTerm, activeTab]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  useEffect(() => {
    filterSchools();
  }, [filterSchools]);

  const {
    page: approvalPage,
    setPage: setApprovalPage,
    totalPages: approvalTotalPages,
    paginatedItems: pagedApprovalSchools,
    totalItems: approvalFilteredTotal,
    pageSize: approvalPageSize,
    setPageSize: setApprovalPageSize,
  } = useKarunaPagedList(filteredSchools, searchTerm, activeTab);

  const handleApprove = async (schoolId: string) => {
    setLoading(true);
    try {
      // Find the school in our current state to get all existing data
      const schoolToUpdate = schools.find(s => s.id === schoolId);

      if (!schoolToUpdate) {
        setLoading(false);
        return;
      }

      const approveRecord = async (school: School, silent: boolean) => {
        // Fetch the full record from API to ensure we have all fields
        let currentRecord: any;
        try {
          currentRecord = await DynamicModuleService.getItemById(
            SCHEMA_NAME,
            school.id
          );
          if (import.meta.env.DEV) {
            console.log("📋 Current record from API:", currentRecord);
          }
        } catch (fetchError) {
          if (import.meta.env.DEV) {
            console.warn(
              "⚠️ Could not fetch full record, using state data:",
              fetchError
            );
          }
          currentRecord = school;
        }

        // Remove meta fields first
        const cleanedData = removeMetaFields(currentRecord);

        // Build update payload - use cleaned data and only update status
        const updatePayload: any = { ...cleanedData };
        applySchoolProfileStatus(updatePayload, "approved");

        // Remove any undefined or null values that might cause issues
        Object.keys(updatePayload).forEach(key => {
          if (updatePayload[key] === undefined || updatePayload[key] === null) {
            delete updatePayload[key];
          }
        });

        if (import.meta.env.DEV) {
          console.log("💾 Updating with complete payload:", updatePayload);
        }

        await DynamicModuleService.updateItem(
          SCHEMA_NAME,
          school.id,
          updatePayload,
          { silent }
        );
      };

      await approveRecord(schoolToUpdate, false);

      // Duplicate registrations share a KC No; approve the pending copies too
      const kcno = (schoolToUpdate.kcno ?? "").trim().toLowerCase();
      const duplicates = kcno
        ? schools.filter(
            s =>
              s.id !== schoolId &&
              (s.kcno ?? "").trim().toLowerCase() === kcno &&
              normalizeSchoolApprovalStatus(s.status ?? "") === "pending"
          )
        : [];
      const duplicateResults = await Promise.allSettled(
        duplicates.map(s => approveRecord(s, true))
      );
      const approvedIds = new Set([
        schoolId,
        ...duplicates
          .filter((_, i) => duplicateResults[i].status === "fulfilled")
          .map(s => s.id),
      ]);
      duplicateResults.forEach((result, i) => {
        if (result.status === "rejected") {
          console.error(
            `❌ Error approving duplicate school ${duplicates[i].id}:`,
            result.reason
          );
        }
      });

      setSchools(prev =>
        prev.map(s =>
          approvedIds.has(s.id) ? { ...s, status: "approved" } : s
        )
      );
      fetchSchools();
    } catch (error: any) {
      console.error("❌ Error approving school:", error);
      console.error("❌ Error response:", error?.response?.data);

      let errorMessage = "Failed to approve school";
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage =
            typeof errorData.error === "string"
              ? errorData.error
              : (errorData.error.message ?? errorMessage);
        } else if (typeof errorData === "object") {
          const fieldErrors: string[] = [];
          Object.keys(errorData).forEach(key => {
            if (Array.isArray(errorData[key])) {
              fieldErrors.push(`${key}: ${errorData[key].join(", ")}`);
            } else if (typeof errorData[key] === "string") {
              fieldErrors.push(`${key}: ${errorData[key]}`);
            }
          });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join("; ");
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      console.error("❌ Approve error message:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSchool || !rejectionReason.trim()) {
      return;
    }

    setLoading(true);
    try {
      // Find the school in our current state to get all existing data
      const schoolToUpdate =
        schools.find(s => s.id === selectedSchool.id) ?? selectedSchool;

      // Fetch the full record from API to ensure we have all fields
      let currentRecord: any;
      try {
        currentRecord = await DynamicModuleService.getItemById(
          SCHEMA_NAME,
          selectedSchool.id
        );
        if (import.meta.env.DEV) {
          console.log("📋 Current record from API:", currentRecord);
        }
      } catch (fetchError) {
        if (import.meta.env.DEV) {
          console.warn(
            "⚠️ Could not fetch full record, using state data:",
            fetchError
          );
        }
        currentRecord = schoolToUpdate;
      }

      // Remove meta fields first
      const cleanedData = removeMetaFields(currentRecord);

      // Build update payload - use cleaned data and update status + rejection reason
      const updatePayload: any = {
        ...cleanedData,
        rejection_reason: rejectionReason,
      };
      applySchoolProfileStatus(updatePayload, "rejected");

      // Remove any undefined or null values that might cause issues
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined || updatePayload[key] === null) {
          delete updatePayload[key];
        }
      });

      if (import.meta.env.DEV) {
        console.log("💾 Updating with complete payload:", updatePayload);
      }

      await DynamicModuleService.updateItem(
        SCHEMA_NAME,
        selectedSchool.id,
        updatePayload
      );

      const rejectedId = selectedSchool.id;
      setSchools(prev =>
        prev.map(s =>
          s.id === rejectedId
            ? { ...s, status: "rejected", rejection_reason: rejectionReason }
            : s
        )
      );
      fetchSchools();
      setShowRejectDialog(false);
      setRejectionReason("");
      setSelectedSchool(null);
    } catch (error: any) {
      console.error("❌ Error rejecting school:", error);
      console.error("❌ Error response:", error?.response?.data);

      // Extract error message from different possible structures
      let _errorMessage = "Failed to reject school";
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          _errorMessage = errorData;
        } else if (errorData.message) {
          _errorMessage = errorData.message;
        } else if (errorData.detail) {
          _errorMessage = errorData.detail;
        } else if (errorData.error) {
          _errorMessage =
            typeof errorData.error === "string"
              ? errorData.error
              : (errorData.error.message ?? _errorMessage);
        } else if (typeof errorData === "object") {
          // Try to extract field-specific errors
          const fieldErrors: string[] = [];
          Object.keys(errorData).forEach(key => {
            if (Array.isArray(errorData[key])) {
              fieldErrors.push(`${key}: ${errorData[key].join(", ")}`);
            } else if (typeof errorData[key] === "string") {
              fieldErrors.push(`${key}: ${errorData[key]}`);
            }
          });
          if (fieldErrors.length > 0) {
            _errorMessage = fieldErrors.join("; ");
          }
        }
      } else if (error?.message) {
        _errorMessage = error.message;
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const stats = {
    pending: schools.filter(
      s => normalizeSchoolApprovalStatus(s.status ?? "") === "pending"
    ).length,
    approved: schools.filter(
      s => normalizeSchoolApprovalStatus(s.status ?? "") === "approved"
    ).length,
    rejected: schools.filter(
      s => normalizeSchoolApprovalStatus(s.status ?? "") === "rejected"
    ).length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(getLink(navigationRoutes.ADMIN_SCHOOLS))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">School Approvals</h1>
            <p className="text-muted-foreground">
              Review and approve school registrations
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Pending
                </p>
                <h3 className="text-2xl font-bold">{stats.pending}</h3>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Approved
                </p>
                <h3 className="text-2xl font-bold">{stats.approved}</h3>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Rejected
                </p>
                <h3 className="text-2xl font-bold">{stats.rejected}</h3>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by school name, KC No, or email..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex gap-2 border-b">
              {(["pending", "approved", "rejected"] as const)
                .filter(tab => {
                  // If viewing history, only show approved and rejected tabs
                  if (isHistoryView) {
                    return tab === "approved" || tab === "rejected";
                  }
                  // If viewing pending only, only show pending tab
                  if (isPendingOnlyView) {
                    return tab === "pending";
                  }
                  // Default: show all tabs
                  return true;
                })
                .map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? "border-b-2 border-primary text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {tab} ({stats[tab]})
                  </button>
                ))}
            </div>

            <div className="space-y-4">
              {loading && filteredSchools.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : filteredSchools.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  No {activeTab} schools found
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <ListCardViewToggle
                      viewMode={viewMode}
                      onViewChange={setViewMode}
                    />
                  </div>

                  {viewMode === "card" ? (
                    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {pagedApprovalSchools.map(school => (
                        <Card
                          key={school.id}
                          className="flex flex-col overflow-hidden border-border/60 shadow-sm"
                        >
                          <div className="border-b border-border/60 bg-muted/30 px-4 py-3">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0 flex-1 space-y-1.5">
                                <h3 className="text-base font-semibold leading-tight tracking-tight">
                                  {school.schoolname ?? "N/A"}
                                </h3>
                                <Badge
                                  className={`w-fit border-0 font-normal ${getStatusColor(
                                    school.status ?? "pending"
                                  )}`}
                                >
                                  {school.status ?? "pending"}
                                </Badge>
                              </div>
                              {activeTab === "pending" && (
                                <div className="flex w-full shrink-0 gap-2 sm:w-auto sm:justify-end">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="flex-1 sm:flex-initial"
                                    onClick={() => handleApprove(school.id)}
                                    disabled={loading}
                                  >
                                    <CheckCircle className="mr-1 h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="flex-1 sm:flex-initial"
                                    onClick={() => {
                                      setSelectedSchool(school);
                                      setShowRejectDialog(true);
                                    }}
                                    disabled={loading}
                                  >
                                    <XCircle className="mr-1 h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-0 px-4 py-3">
                            <div className="space-y-3">
                              <ApprovalFieldRow label="KC No">
                                {school.kcno}
                              </ApprovalFieldRow>
                              <ApprovalFieldRow label="Principal">
                                {school.principal}
                              </ApprovalFieldRow>
                              <ApprovalFieldRow label="Contact">
                                {school.contact}
                              </ApprovalFieldRow>
                              <ApprovalFieldRow label="School Email">
                                {school.schoolemail}
                              </ApprovalFieldRow>
                              <ApprovalFieldRow label="Kendra">
                                {school.kendra}
                              </ApprovalFieldRow>
                            </div>

                            {(school.address1 ??
                              school.city ??
                              school.state) && (
                              <div className="mt-3 rounded-md border border-border/50 bg-muted/20 p-3">
                                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Address
                                </p>
                                <div className="space-y-3">
                                  {school.address1 && (
                                    <ApprovalFieldRow label="Address 1">
                                      {school.address1}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.address2 && (
                                    <ApprovalFieldRow label="Address 2">
                                      {school.address2}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.city && (
                                    <ApprovalFieldRow label="City">
                                      {school.city}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.pincode && (
                                    <ApprovalFieldRow label="Pin-code">
                                      {school.pincode}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.state && (
                                    <ApprovalFieldRow label="State">
                                      {school.state}
                                    </ApprovalFieldRow>
                                  )}
                                </div>
                              </div>
                            )}

                            {(school.dateofformation ??
                              school.contactfax ??
                              school.emailid ??
                              school.mobileno) && (
                              <div className="mt-3 rounded-md border border-border/50 bg-muted/20 p-3">
                                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Additional Contact Info
                                </p>
                                <div className="space-y-3">
                                  {school.dateofformation && (
                                    <ApprovalFieldRow label="Date of Formation">
                                      {school.dateofformation}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.contactfax && (
                                    <ApprovalFieldRow label="Contact/Fax">
                                      {school.contactfax}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.emailid && (
                                    <ApprovalFieldRow label="Email ID">
                                      {school.emailid}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.mobileno && (
                                    <ApprovalFieldRow label="Mobile No.">
                                      {school.mobileno}
                                    </ApprovalFieldRow>
                                  )}
                                  {school.additionaldetails && (
                                    <ApprovalFieldRow label="Additional Details">
                                      {school.additionaldetails}
                                    </ApprovalFieldRow>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="mt-3 rounded-md border border-border/50 bg-muted/20 p-3">
                              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Teacher in-charge
                              </p>
                              <div className="space-y-3">
                                <ApprovalFieldRow label="Name">
                                  {school.teachername}
                                </ApprovalFieldRow>
                                <ApprovalFieldRow label="Contact">
                                  {school.teachercontact}
                                </ApprovalFieldRow>
                                <ApprovalFieldRow label="Email">
                                  {school.teacheremail}
                                </ApprovalFieldRow>
                              </div>
                            </div>

                            {school.rejection_reason ? (
                              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
                                <span className="font-medium">Reason: </span>
                                <span className="break-words">
                                  {school.rejection_reason}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pagedApprovalSchools.map(school => (
                        <Card
                          key={school.id}
                          className="overflow-hidden border-border/60 shadow-sm"
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                              <div className="min-w-0 flex-1 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="font-semibold leading-snug">
                                    {school.schoolname ?? "N/A"}
                                  </h3>
                                  <Badge
                                    className={`border-0 font-normal ${getStatusColor(
                                      school.status ?? "pending"
                                    )}`}
                                  >
                                    {school.status ?? "pending"}
                                  </Badge>
                                </div>
                                <p className="break-words text-sm text-muted-foreground">
                                  <span className="font-medium text-foreground/80">
                                    KC
                                  </span>{" "}
                                  {school.kcno ?? "—"}
                                  <span className="mx-1.5 text-border">|</span>
                                  <span className="font-medium text-foreground/80">
                                    Principal
                                  </span>{" "}
                                  {school.principal ?? "—"}
                                </p>
                                <p className="break-words text-sm text-muted-foreground">
                                  <span className="font-medium text-foreground/80">
                                    Contact
                                  </span>{" "}
                                  {school.contact ?? "—"}
                                  <span className="mx-1.5 text-border">|</span>
                                  <span className="font-medium text-foreground/80">
                                    Email
                                  </span>{" "}
                                  {school.schoolemail ?? "—"}
                                </p>
                                {school.rejection_reason && (
                                  <p className="break-words text-sm text-red-600">
                                    {school.rejection_reason}
                                  </p>
                                )}
                              </div>
                              {activeTab === "pending" && (
                                <div className="flex shrink-0 gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleApprove(school.id)}
                                    disabled={loading}
                                  >
                                    <CheckCircle className="mr-1 h-4 w-4" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      setSelectedSchool(school);
                                      setShowRejectDialog(true);
                                    }}
                                    disabled={loading}
                                  >
                                    <XCircle className="mr-1 h-4 w-4" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  <KarunaPagination
                    page={approvalPage}
                    totalPages={approvalTotalPages}
                    totalItems={approvalFilteredTotal}
                    pageSize={approvalPageSize}
                    onPageChange={setApprovalPage}
                    onPageSizeChange={setApprovalPageSize}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject School Registration</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting{" "}
              {selectedSchool?.schoolname ?? "this school"}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason("");
                setSelectedSchool(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={loading}
            >
              Reject School
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
